import ReactApexChart from "react-apexcharts";
import { Box, Typography } from "@mui/material";
import PendingSkeleton from "src/components/pages/dashboard/skeleton/dashboard/PendingTable";
import { UseDashboardContext } from "src/context/dashboard";
import useStyles from "./Styles";

type ChatData = {
  count: number;
  date: string;
  pageId: string;
  pageName: string;
  source: "facebook" | "whatsapp" | "instagram";
};

const LineChart = () => {
  const contextData = UseDashboardContext();
  const { classes } = useStyles();

  let totalChatsGraphDataLoading = false;
  let totalChatsGraphData: ChatData[] = [];

  if (contextData) {
    ({ totalChatsGraphData, totalChatsGraphDataLoading } = contextData);
  }

  const sources = ["facebook", "whatsapp", "instagram", "chatbot"];
  const dates = Array.from(
    new Set(totalChatsGraphData.map((d) => d.date)),
  ).sort();
  const pageNames = Array.from(
    new Set(totalChatsGraphData.map((d) => d.pageName)),
  );

  // ✅ Only keep (date|source) combos that have data
  const xAxisCategories = dates.flatMap(
    (date) =>
      sources
        .map((source) => {
          const totalForGroup = totalChatsGraphData.filter(
            (item) => item.date === date && item.source === source,
          );
          const totalCount = totalForGroup.reduce(
            (acc, item) => acc + item.count,
            0,
          );
          return totalCount > 0 ? `${date}|${source}` : null;
        })
        .filter(Boolean) as string[],
  );

  // ✅ Build stacked series per page
  const series = pageNames.map((pageName) => {
    const data = xAxisCategories.map((cat) => {
      const [date, source] = cat.split("|");
      const match = totalChatsGraphData.find(
        (item) =>
          item.date === date &&
          item.source === source &&
          item.pageName === pageName,
      );
      return match ? match.count : 0;
    });

    return { name: pageName, data };
  });

  // ✅ Calculate dynamic max Y and ticks
  const allValues = series.flatMap((s) => s.data);
  const maxY = Math.max(...allValues, 1);
  const tickAmount = Math.min(maxY, 5);

  const options = {
    chart: {
      type: "bar" as const,
      stacked: true,
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout" as const,
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: false, // ✅ Prevent resize layout crash
        },
      },
      redrawOnWindowResize: false, // ✅ Prevent sidebar hover crash
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30px",
      },
    },
    xaxis: {
      categories: xAxisCategories,
      labels: {
        rotate: 0,
        // formatter: (val: string) => val.split("|")[0], // show only date under each bar
        formatter: (val: any) => {
          if (typeof val === "string" && val.includes("|")) {
            return val.split("|")[0]; // ✅ Only show the date part
          }
          return val;
        },
        style: {
          fontFamily: "Poppins",
          fontSize: "14px",
          colors: "#999999",
        },
      },
      crosshairs: {
        show: false, // ✅ This disables the vertical hover highlight line
      },
    },
    yaxis: {
      min: 0,
      max: maxY, // ✅ Explicitly set max to avoid internal null tick calculation
      tickAmount,
      decimalsInFloat: 0,
      labels: {
        formatter: (val: number) => (val !== null ? `${Math.round(val)}` : "0"),
        style: {
          fontFamily: "Poppins",
          fontSize: "14px",
          colors: "#999999",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom({ dataPointIndex, w }: any) {
        if (!w || !w.globals || !w.globals.initialSeries) return "";
        const labels = w.globals.initialSeries
          .map((s: any, i: any) => {
            const dataValue = s.data?.[dataPointIndex];
            const value = typeof dataValue === "number" ? dataValue : 0;
            if (value > 0) {
              return `<div style="margin: 2px 0;">
                        <span style="display:inline-block;width:10px;height:10px;background:${
                          w.config.colors?.[i] || "#ccc"
                        };border-radius:50%;margin-right:8px;"></span>
                        ${s.name}: <strong>${value}</strong>
                      </div>`;
            }
            return "";
          })
          .join("");

        const label = w.globals.labels?.[dataPointIndex];
        if (!label || typeof label !== "string") return "";

        const [date, source] = label.split("|");
        return `<div style="padding: 8px 10px; font-family: Poppins; font-size: 13px;">
                  <div style="margin-bottom: 4px; font-weight: 600;">
                    ${date} - ${source}
                  </div>
                  ${labels}
                </div>`;
      },
    },
    legend: {
      show: true,
      position: "bottom" as const,
    },
    dataLabels: {
      enabled: false,
    },
    colors: series.map((s, i) => `hsl(${(i * 45) % 360}, 70%, 55%)`),
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Typography className={classes.headerLeftText1} variant="h6">
            Statistics
          </Typography>
          <Typography className={classes.headerLeftText2} variant="h6">
            Pages Breakdowns
          </Typography>
        </Box>
      </Box>

      {totalChatsGraphDataLoading && (
        <Box className={classes.loadingBox}>
          <PendingSkeleton />
        </Box>
      )}
      {!totalChatsGraphDataLoading &&
      series.length > 0 &&
      xAxisCategories.length > 0 ? (
        <ReactApexChart
          height={400}
          type="bar"
          series={series}
          options={options}
        />
      ) : (
        !totalChatsGraphDataLoading && (
          <Box className={classes.loadingBox}>
            <Typography variant="body2" color="textSecondary">
              No data available for the selected period
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default LineChart;
